import { memo, useCallback, useMemo } from 'react';

import { type SegmentedControlOption } from '@air/component-segmented-control';

import { SegmentedControlRow } from '~/components/ShareModal/components/SegmentedControlRow';
import { UpsellFlashIconButton } from '~/components/ShareModal/components/UpsellFlashIconButton';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

export type CustomFieldsRowProps = {
  canViewCustomFields: boolean;
  canEditCustomFields: boolean;
  canView: boolean;
  canManageApplications: boolean;
  disabled?: boolean;
  isLoading: boolean;
  showMetadata: boolean;
  onChange: (next: { canView: boolean; canManageApplications: boolean }) => void;
  'data-testid'?: string;
};

export const CustomFieldsRow = memo(
  ({
    canViewCustomFields,
    canEditCustomFields,
    canView,
    canManageApplications,
    disabled = false,
    isLoading,
    showMetadata,
    onChange,
    'data-testid': dataTestId,
  }: CustomFieldsRowProps) => {
    const { customFieldsEnabled } = useShareSettings();

    const isGated = disabled || !showMetadata || !customFieldsEnabled || isLoading;

    const selectedValue = useMemo(() => {
      if (!showMetadata) {
        return 'hide';
      }

      if (canView && canManageApplications) {
        return 'editable';
      }

      if (canView) {
        return 'read-only';
      }

      return 'hide';
    }, [showMetadata, canView, canManageApplications]);

    const options = useMemo((): SegmentedControlOption[] => {
      const next: SegmentedControlOption[] = [
        { label: 'Hide', value: 'hide', id: 'hide', tooltip: 'Hide', disabled: isGated },
      ];

      const canSelectReadOnly = canViewCustomFields && !(canManageApplications && !canEditCustomFields);
      const includeReadOnlyForState = selectedValue === 'read-only' && !canSelectReadOnly;

      if (canSelectReadOnly || includeReadOnlyForState) {
        next.push({
          label: 'Read-only',
          value: 'read-only',
          id: 'read-only',
          tooltip: 'Read-only',
          disabled: includeReadOnlyForState ? true : isGated,
        });
      }

      const includeEditableForState = selectedValue === 'editable' && !canEditCustomFields;

      if (canEditCustomFields || includeEditableForState) {
        next.push({
          label: 'Editable',
          value: 'editable',
          id: 'editable',
          tooltip: 'Editable',
          disabled: includeEditableForState ? true : isGated,
        });
      }

      return next;
    }, [canEditCustomFields, canViewCustomFields, canManageApplications, isGated, selectedValue]);

    const selectedOption = useMemo(
      () => options.find((option) => option.value === selectedValue) ?? options[0],
      [options, selectedValue],
    );

    const handleSelect = useCallback(
      (option: SegmentedControlOption) => {
        if (option.value === 'hide') {
          onChange({ canView: false, canManageApplications: false });
        } else if (option.value === 'read-only') {
          onChange({ canView: true, canManageApplications: false });
        } else if (option.value === 'editable') {
          onChange({ canView: true, canManageApplications: true });
        }
      },
      [onChange],
    );

    const labelAccessory = useMemo(
      () =>
        !customFieldsEnabled ? <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton> : undefined,
      [customFieldsEnabled],
    );

    return (
      <SegmentedControlRow
        data-testid={dataTestId}
        label="Custom fields"
        labelAccessory={labelAccessory}
        onSelect={handleSelect}
        options={options}
        selectedOption={selectedOption}
      />
    );
  },
);

CustomFieldsRow.displayName = 'CustomFieldsRow';
